import {
  createAsyncThunk,
  createEntityAdapter,
  createSlice,
  EntityState,
} from "@reduxjs/toolkit";
import { RootState } from "model";

export type AccessibleFarmerDashboard = {
  guid: string;
  name: string;
  dashboards: { visualizationId: string; title: string }[];
};

export default class AccessibleFarmerDashboardsAPI {
  async getIds(): Promise<Array<AccessibleFarmerDashboard>> {
    const res = await fetch(
      `${process.env.REACT_APP_API_ENDPOINT}/v1/getDashboards/accessibleFarmerDashboardIds`,
      {
        method: "GET",
        headers: {
          Authorization: `Bearer ${window.localStorage.getItem("jwt") || ""}`,
          "Content-Type": "application/json",
        },
      }
    );
    const resultJson = await res.json();
    return resultJson as AccessibleFarmerDashboard[];
  }
}

const accessibleFarmerDashboardsApi = new AccessibleFarmerDashboardsAPI();

export const fetchAllAccessibleFarmerDashboards = createAsyncThunk(
  "accessibleFarmerDashboards/fetchIds",
  async () => {
    const fetchedDashboardIds = await accessibleFarmerDashboardsApi.getIds();
    return fetchedDashboardIds;
  }
);

const accessibleFarmerDashboardsAdapter =
  createEntityAdapter<AccessibleFarmerDashboard>({
    selectId: (projectDashboard) => projectDashboard.guid,
  });

export const {
  selectAll: selectAllAccessibleFarmerDashboards,
  selectById: selectAccessibleFarmerDashboardsByAccessibleUserId,
  selectEntities: selectAccessibleFarmerDashboardsEntities,
  selectIds: selectAccessibleFarmerDashboardIds,
} = accessibleFarmerDashboardsAdapter.getSelectors(
  (state: RootState) => state.accessibleFarmerDashboards
);

type Status = "success" | "error" | null;
type LoadingState = "idle" | "pending";

export type accessibleFarmerDashboardsState =
  EntityState<AccessibleFarmerDashboard> & {
    loading: LoadingState;
    error: null;
    status: Status;
  };

const initialAccessibleFarmerDashboardsState: accessibleFarmerDashboardsState =
  accessibleFarmerDashboardsAdapter.getInitialState({
    loading: "idle",
    error: null,
    status: null,
  });

const accessibleFarmerDashboardsSlice = createSlice({
  name: "accessibleFarmerDashboards",
  initialState: initialAccessibleFarmerDashboardsState,
  reducers: {
    resetAccessibleFarmerDashboards: () =>
      initialAccessibleFarmerDashboardsState,
  },
  extraReducers: {
    [fetchAllAccessibleFarmerDashboards.fulfilled.type]: (
      state,
      { payload }
    ) => {
      for (const dashboardMetadata of payload) {
        accessibleFarmerDashboardsAdapter.upsertOne(state, {
          guid: dashboardMetadata.userGuid,
          name: dashboardMetadata.userName,
          dashboards: dashboardMetadata.dashboards,
        });
      }
    },
  },
});

const { actions, reducer } = accessibleFarmerDashboardsSlice;

export const { resetAccessibleFarmerDashboards } = actions;
export const accessibleFarmerDashboardsReducer = reducer;
